import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import { DateCell } from '../../../../components/table/DateCell';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { auditLogsApi } from '../auditLogsApi';
import type { AuditLogListItem } from '../auditLogTypes';

/**
 * The global Audit Logs page — frontend reference §13.
 *
 * Read-only by construction: the shell's create/edit/delete affordances are simply not
 * configured, because the rows are append-only.
 *
 * Clicking a log opens its profile page (`/audit-logs/:id`) — the whole captured activity,
 * not the request-context subset a modal holds. That is a recorded deviation from §13's
 * "every row opens the detail modal"; the modal remains the depth inside the per-module
 * History Logs modal, which cannot navigate away without losing the list behind it.
 */
export function AuditLogsIndexPage() {
  const navigate = useNavigate();

  const openEntry = useCallback(
    (row: AuditLogListItem) => navigate(`/audit-logs/${row.id}`),
    [navigate],
  );

  const fetchLogs = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      auditLogsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          // Every facet the rail offers is sent. A facet the server does not read renders
          // values and counts, then filters nothing when clicked — a control that lies.
          category: query.facets.category?.join(','),
          action: query.facets.action?.join(','),
          method: query.facets.method?.join(','),
        },
        signal,
      ),
    [],
  );

  return (
    <IndexPage<AuditLogListItem>
      module="audit-logs"
      /*
        The one opt-out. This table's Timestamp column already IS the creation time, and
        Actioner already IS the creator — appending them again would print each twice.
      */
      auditColumns={false}
      breadcrumb={['Administration', 'Audit Logs']}
      columns={COLUMNS}
      /*
        Coarse to fine. Every value list is derived from the rows themselves, so nothing
        needs an "All" entry: no selection IS all, and the rail's own "Clear N filters"
        button is how a reader gets back to it.
      */
      facets={[
        { key: 'method', label: 'Request type' },
        { key: 'category', label: 'Category' },
        { key: 'action', label: 'Action' },
      ]}
      auditEntityTypes={['AuditLog']}
      rowKey={(row) => String(row.id)}
      fetch={fetchLogs}
      searchPlaceholder="Search actor, record, or path"
      emptyMessage="No recorded activity yet."
      onRowOpen={openEntry}
      rowActions={(row) => [
        {
          key: `view-${row.id}`,
          label: 'View activity',
          onClick: () => openEntry(row),
        },
      ]}
      otherInfo={(row) => ({
        // The trail is not itself audited; these mirror the event's own timestamp.
        createdAt: row.timestamp,
        createdByName: row.userName ?? row.userEmail,
        updatedAt: null,
        updatedByName: null,
      })}
    />
  );
}

/**
 * Columns map to `AuditLogListItem`.
 *
 * Actioner leads, ahead of Timestamp — a recorded deviation from §13's column order. It puts
 * the person in the anchor column, which is the row's identity and its click target, and
 * leaves the timestamp beside it where a list already sorted by recency reads as a sequence.
 */
const COLUMNS: ColumnDef<AuditLogListItem>[] = [
  {
    key: 'actioner',
    header: 'Actioner',
    width: 200,
    flex: true,
    render: (row) => <TextCell value={row.userName ?? (row.userEmail ? null : 'anonymous')} />,
  },
  {
    key: 'timestamp',
    header: 'Timestamp',
    width: 180,
    render: (row) => <DateCell value={row.timestamp} />,
  },
  {
    key: 'actionerEmail',
    header: 'Email',
    width: 230,
    render: (row) => <TextCell value={row.userEmail} mono />,
  },
  /*
    The three values the list view puts under the title — what happened, to what, over
    which request. Nominated rather than left to the default, which would have taken the
    timestamp and the email: both are already the row's own identity here, and the list
    would have printed each of them twice.
  */
  {
    key: 'action',
    header: 'Action',
    width: 190,
    summary: true,
    render: (row) => <TextCell value={row.action} />,
  },
  {
    key: 'request',
    header: 'Request',
    width: 260,
    summary: true,
    render: (row) => (
      <TextCell value={row.httpMethod ? `${row.httpMethod} ${row.path ?? ''}`.trim() : null} mono />
    ),
  },
  {
    key: 'entity',
    header: 'Entity',
    width: 230,
    summary: true,
    render: (row) => (
      <TextCell
        value={row.entityType ? [row.entityType, row.entityDisplay].filter(Boolean).join(' · ') : null}
      />
    ),
  },
  {
    key: 'duration',
    header: 'Duration',
    width: 110,
    align: 'right',
    render: (row) => <TextCell value={row.durationMs != null ? `${row.durationMs} ms` : null} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 130,
    status: true,
    render: (row) =>
      row.statusCode ? (
        <StatusText tone={row.succeeded ? 'active' : 'blocked'}>
          {row.statusCode} {row.succeeded ? 'OK' : 'Failed'}
        </StatusText>
      ) : (
        <TextCell value={null} />
      ),
  },
];
